// @flow
import React from 'react';
import type { Node } from 'react';
import find from 'lodash/find';
import { useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import { FilterService } from '@riseart/filter-service';
import { components as CONFIG_COMPONENTS } from 'Config';
import {
  collection as COLLECTION_ENUM,
  filter as FILTER_ENUM,
  meta as META_ENUM,
  HTTP_STATUS,
} from 'Enum';
import { pathToParams } from 'shared_services/riseart/utils/FilterRouteUtils';
import { useUrlParams } from 'shared_data/providers/url/useUrlParams';
import { useQuery } from 'shared_services/apollo/useQuery';
import { LocalizedConfig } from 'shared_services/riseart/utils/LocalizedConfig';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { gql404RedirectHandler, getLocaleConfig } from 'shared_services/riseart/utils/RouteUtils';
import { LocationManager } from 'shared_services/riseart/url/Location';
import { LayoutDefault } from 'shared_components/layouts/Default';
import { generateSrcForCDN } from 'shared_components/common/artdirection/picture/Picture';
import { CollectionDetailsArtContainer } from 'shared_components/collections/containers/Art';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { CollectionInfo } from 'shared_components/collections/containers/info/Info';
import { MetaProvider } from 'shared_data/providers/meta/Meta';
import { getFilterDomainConfig } from 'shared_models/configs/filters/domains';
import READ_COLLECTION_QUERY from 'shared_data/queries/collection/read.graphql';
import { META_SUBSCRIBER_NAME } from 'shared_services/riseart/meta/Meta';
import { LDTYPES, generateLDJSON } from 'shared_models/seo/ldJSON';

const MAIN_SQUARE_FALLBACK_IMAGE = find(CONFIG_COMPONENTS.collections.fallbackImages, {
  type: COLLECTION_ENUM.image.type.TYPE_MAIN_SQUARE,
});

/**
 * generateHrefLangs
 *
 * @param {Object} data
 * @param {Object} location
 * @returns {Array<Object>}
 */
function generateHrefLangs(data: Object, location: Object): Object | Array<Object> {
  const FILTER_DOMAIN = FILTER_ENUM.domain.COLLECTION_ART;

  // Create hreflangs based on provided localized slugs from backend
  const hreflangUrls = data.locales.reduce(
    (accumulator, { localeCode, slug }) => {
      const locale = getLocaleConfig(localeCode, 'name');

      // Unrecognized locale
      if (!locale) {
        return accumulator;
      }

      // FilterService instance to parse the filter data from requested url
      const routeFilterService = new FilterService({
        ...getFilterDomainConfig(FILTER_DOMAIN, { id: data.id, slug }),
      });
      const filterData = routeFilterService.route(pathToParams(location.pathname), location.search);

      // FilterService instance to assemble new urls for hreflang by provided locale
      const assembleFilterService = new FilterService({
        ...getFilterDomainConfig(FILTER_DOMAIN, { id: data.id, slug, locale }),
        locale: locale.name,
      });
      const localeUrl = assembleFilterService.assemble(filterData.filters);

      // Generate absolute url
      accumulator.absolute[localeCode] = localeUrl
        ? `${LocationManager.get('origin')}${localeUrl}`
        : null;

      // Generate relative url
      accumulator.relative[localeCode] = localeUrl || null;

      return accumulator;
    },
    { absolute: {}, relative: {} },
  );

  if (hreflangUrls) {
    return {
      [META_ENUM.METATYPE.LINK_HREFLANG]: {
        type: 'generated',
        href: hreflangUrls.absolute,
      },
      [META_ENUM.METATYPE.LINK_HREFLANG_REDIRECT]: {
        type: 'generated',
        href: hreflangUrls.relative,
      },
    };
  }

  return [];
}

type Props = { routeConfig: Object, staticContext: ?Object };

/**
 * CollectionDetailsPage
 *
 * @param {Props} props
 * @returns {Node}
 */
export default function CollectionDetailsPage({ staticContext, routeConfig }: Props): Node {
  const { formatMessage } = useIntl();
  const { id, slug } = useParams();
  const history = useHistory();
  const { translatedLocation: location } = useUrlParams();
  const {
    loading,
    error,
    data: queryResponse = {},
  } = useQuery(READ_COLLECTION_QUERY, {
    variables: { id },
    context: {
      customOptions: {
        errorSuppressFromResponse: true,
        errorHandler: gql404RedirectHandler(
          UrlAssembler.collectionList({
            search: {
              [LocalizedConfig.get('navigation.uri.query.params.message')]: HTTP_STATUS.NOT_FOUND,
            },
          }),
          history,
          staticContext,
        ),
      },
    },
  });

  const data = queryResponse.readCollection;
  const hasError = !!error || !data;
  const canonicalURL = data
    ? UrlAssembler.collectionDetail(data.id, data.slug, {
        origin: LocationManager.get('origin'),
      })
    : LocationManager.get('originWithPath');
  const authorUrl =
    data &&
    data.author &&
    UrlAssembler.authorDetail(data.author.type, data.author.typeId, data.author.typeAlias);

  return (
    <LayoutDefault>
      {loading ? <IsomorphicRipple isActive isFullScreen /> : null}
      <MetaProvider
        metaSubscriber={META_SUBSCRIBER_NAME.COLLECTION_META}
        meta={
          data
            ? {
                [META_ENUM.METATYPE.LINK_CANONICAL]: canonicalURL,
                [META_ENUM.METATYPE.LINK_CANONICAL_REDIRECT]: UrlAssembler.collectionDetail(
                  data.id,
                  data.slug,
                  {
                    ...(location.search ? { search: location.search } : null),
                    ...(location.hash ? { hash: location.hash } : null),
                  },
                ),
                [META_ENUM.METATYPE.META_TITLE]: formatMessage(
                  { id: 'meta.collectionDetails.dynamicMetaTitle' },
                  { name: data.name },
                ),
                [META_ENUM.METATYPE.META_DESCRIPTION]: formatMessage(
                  { id: 'meta.collectionDetails.dynamicMetaDescription' },
                  { name: data.name },
                ),
                [META_ENUM.METATYPE.PAGE_TITLE]: {
                  disabled: true,
                  text: formatMessage(
                    { id: 'meta.collectionDetails.dynamicPageTitle' },
                    { name: data.name },
                  ),
                },
                [META_ENUM.METATYPE.META_OG_TITLE]: formatMessage(
                  { id: 'components.share.messages.collection_detail.facebook' },
                  { name: data.name },
                ),
                [META_ENUM.METATYPE.META_OG_DESCRIPTION]: formatMessage(
                  { id: 'meta.collectionDetails.dynamicMetaDescription' },
                  { name: data.name },
                ),
                [META_ENUM.METATYPE.META_OG_URL]: canonicalURL,
                [META_ENUM.METATYPE.META_OG_IMAGE]: generateSrcForCDN({
                  ...(find(data.images, {
                    type: COLLECTION_ENUM.image.type.TYPE_MAIN_SQUARE,
                  }) || MAIN_SQUARE_FALLBACK_IMAGE),
                  width: 1200,
                }),
                [META_ENUM.METATYPE.SCRIPT_LD_JSON_COLLECTION]: generateLDJSON(LDTYPES.COLLECTION, {
                  ...data,
                  author: {
                    ...data.author,
                    url: authorUrl ? `${LocationManager.get('origin')}${authorUrl}` : null,
                  },
                  mainEntityOfPage: {
                    type: 'WebPage',
                    url: canonicalURL,
                  },
                }),
                ...(data.hide
                  ? {
                      [META_ENUM.METATYPE.META_ROBOTS]: META_ENUM.ATTRIBUTE_CONTENT.NO_INDEX,
                    }
                  : {}),
                ...(data.meta || {}),
              }
            : null
        }
        hasError={hasError}
      />
      <MetaProvider
        metaSubscriber={META_SUBSCRIBER_NAME.COLLECTION_LOCALE_META}
        meta={data ? generateHrefLangs(data, location) : null}
        hasError={hasError}
      />
      {data ? (
        <CollectionInfo
          id={data.id}
          title={data.name}
          description={data.description}
          image={data.images}
          avatarImages={data.author.avatars}
          authorName={data.author.fullName}
          authorUrl={authorUrl}
          biography={data.author.biography}
        />
      ) : null}
      <CollectionDetailsArtContainer
        routeConfig={routeConfig}
        disableHreflangMeta
        urlParams={{
          id,
          slug,
          url: UrlAssembler.collectionDetail(id, slug),
        }}
      />
    </LayoutDefault>
  );
}
